import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import { useAuth } from "../context/AuthContext";
import {
  MdSchedule, MdSend, MdCheckCircle,
  MdError, MdCalendarToday,
} from "react-icons/md";

const ScheduledReports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "NETWORK_ADMINISTRATOR";
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await API.get("/scheduled-reports/history");
      setHistory(res.data);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setMessage("");
    try {
      await API.post("/scheduled-reports/send", { period: selectedPeriod });
      setMessage("Report sent successfully to " + process.env.REACT_APP_ALERT_EMAIL);
      await fetchHistory();
    } catch {
      setMessage("Failed to send report. Check email configuration.");
    } finally {
      setSending(false);
    }
  };

  const scheduleInfo = [
    { period: "Daily", time: "Every day at 8:00 AM", icon: "📅" },
    { period: "Weekly", time: "Every Monday at 8:00 AM", icon: "📆" },
    { period: "Monthly", time: "1st of every month at 8:00 AM", icon: "🗓️" },
  ];

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Scheduled Reports
        </h2>
        <p className="text-sm text-gray-500">
          Automated report generation and email delivery
        </p>
      </div>

      {/* Schedule Info Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {scheduleInfo.map((s) => (
          <div key={s.period} className="bg-white rounded-xl shadow p-4">
            <div className="text-2xl mb-2">{s.icon}</div>
            <p className="font-semibold text-gray-800">{s.period} Report</p>
            <p className="text-xs text-gray-500 mt-1">{s.time}</p>
            <p className="text-xs text-blue-500 mt-1">
              Sent to: {process.env.REACT_APP_ALERT_EMAIL || "admin@org.com"}
            </p>
          </div>
        ))}
      </div>

      {/* Manual Trigger */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MdSend size={20} className="text-blue-500" />
            <h3 className="text-md font-semibold text-gray-700">
              Send Report Now
            </h3>
          </div>

          {message && (
            <div
              className={`px-4 py-2 rounded mb-4 text-sm ${
                message.includes("success")
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {message}
            </div>
          )}

          <div className="flex items-center gap-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily Report</option>
              <option value="weekly">Weekly Report</option>
              <option value="monthly">Monthly Report</option>
            </select>
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
            >
              <MdSend size={16} />
              {sending ? "Sending..." : "Send Now"}
            </button>
          </div>
        </div>
      )}

      {/* Report History */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <MdSchedule size={18} className="text-gray-500" />
          <h3 className="text-md font-semibold text-gray-700">
            Report History
          </h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <MdCalendarToday size={40} className="mx-auto mb-2 text-gray-300" />
            <p>No reports sent yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left">Period</th>
                <th className="px-6 py-3 text-left">Sent To</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Date Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 capitalize font-medium text-gray-800">
                    {report.period}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{report.sentTo}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`flex items-center gap-1 text-xs font-medium ${
                        report.status === "SENT"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {report.status === "SENT" ? (
                        <MdCheckCircle size={14} />
                      ) : (
                        <MdError size={14} />
                      )}
                      {report.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(report.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default ScheduledReports;